import React, { useState, useEffect, useRef, useMemo } from "react";

// --- MUI & Toolpad Imports ---
import Box from "@mui/material/Box";
import { createTheme } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { useDemoRouter } from "@toolpad/core/internal";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";

// --- MUI Form & Icon Imports ---
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";

import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";

// --- Swipeable List Imports ---
import {
  SwipeableList,
  SwipeableListItem,
  LeadingActions,
  TrailingActions,
  SwipeAction,
  Type as ListType,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";

// --- Action Icons for Swipe & Drag ---
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";

// --- FullCalendar Imports ---
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, {
  Draggable as FcDraggable,
} from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import jaLocale from "@fullcalendar/core/locales/ja";

// --- DnD Imports ---
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

// --- Firebase Imports ---
import { db, auth, provider } from "./firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";

// --- 共通ユーティリティ ---
const statusToSlug = (status) => {
  const map = {
    未対応: "mitaio",
    対応中: "taiouchu",
    一旦: "ittan",
    完了: "kanryo",
    保留: "horyu",
    連絡待ち: "renrakumachi",
    対応不要: "taiouhuyou",
  };
  return map[status] || String(status).replace(/\s+/g, "-").toLowerCase();
};

const getTodayDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateTime = (str) => {
  if (!str) return { date: getTodayDate(), time: "" };
  if (str.indexOf("T") === -1) return { date: str, time: "" };
  const d = new Date(str);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return { date: `${year}-${month}-${day}`, time: `${hours}:${minutes}` };
};

const toDateTimeStr = (dateObj, isAllDay = false) => {
  if (!dateObj) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  if (isAllDay) return `${year}-${month}-${day}`;
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// --- Task Edit Modal (MUI Dialog) ---
const TaskModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  defaultValues = {},
}) => {
  const isEditing = !!defaultValues.id;
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("未対応");
  const [memo, setMemo] = useState("");
  const [date, setDate] = useState(getTodayDate());
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isAllDay, setIsAllDay] = useState(true);
  const [manualHours, setManualHours] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setTitle(defaultValues.title || "");
      setStatus(defaultValues.status || "未対応");
      setMemo(defaultValues.memo || "");
      setManualHours(defaultValues.estimatedHours || 0);
      setIsAllDay(
        defaultValues.isAllDay !== undefined ? defaultValues.isAllDay : true,
      );

      if (defaultValues.start) {
        const { date: d, time: t } = parseDateTime(defaultValues.start);
        setDate(d);
        setStartTime(t);
      } else {
        setDate(getTodayDate());
        setStartTime("");
      }

      if (defaultValues.end) {
        const { time: t } = parseDateTime(defaultValues.end);
        setEndTime(t);
      } else {
        setEndTime("");
      }
    }
  }, [isOpen, defaultValues]);

  const hasTime = startTime && endTime && !isAllDay;
  let calculatedHours = 0;
  if (hasTime) {
    const s = new Date(`${date}T${startTime}`);
    const e = new Date(`${date}T${endTime}`);
    const diffMs = e - s;
    if (diffMs > 0) calculatedHours = (diffMs / (1000 * 60 * 60)).toFixed(1);
  }
  const estimatedHours = hasTime ? Number(calculatedHours) : manualHours;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    let finalStart = null;
    let finalEnd = null;
    if (date) {
      if (isAllDay) {
        finalStart = date;
      } else {
        finalStart = startTime ? `${date}T${startTime}` : null;
        finalEnd = endTime ? `${date}T${endTime}` : null;
      }
    }
    onSubmit({
      title,
      status,
      start: finalStart,
      end: finalEnd,
      isAllDay,
      estimatedHours: Number(estimatedHours),
      memo,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEditing ? "タスク編集" : "新規タスク"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="タスク名 (必須)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
              variant="outlined"
            />
            <TextField
              select
              label="ステータス"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
            >
              {[
                "未対応",
                "対応中",
                "一旦",
                "完了",
                "保留",
                "連絡待ち",
                "対応不要",
              ].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="工数 (h)"
              type="number"
              value={estimatedHours}
              onChange={(e) => setManualHours(Number(e.target.value))}
              fullWidth
              InputProps={{ readOnly: hasTime }}
              helperText={
                hasTime ? "時間から自動計算されています" : "手動入力可能"
              }
            />
            <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                >
                  日付設定
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAllDay}
                      onChange={(e) => setIsAllDay(e.target.checked)}
                    />
                  }
                  label="終日"
                />
              </Box>
              <TextField
                type="date"
                label="日付"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              {!isAllDay && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="開始"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="終了"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              )}
            </Box>
            <TextField
              label="メモ"
              multiline
              rows={3}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {isEditing && onDelete && (
            <Button
              onClick={() => {
                onDelete(defaultValues.id);
                onClose();
              }}
              color="error"
              variant="outlined"
              sx={{ mr: "auto" }}
            >
              削除
            </Button>
          )}
          <Button onClick={onClose} color="inherit">
            キャンセル
          </Button>
          <Button type="submit" variant="contained" color="primary">
            保存
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// --- TaskList Component (スワイプ改良版) ---
const TaskList = ({
  tasks,
  setTasks,
  setEditingTask,
  setIsModalOpen,
  editTask,
  draggableElRef,
  isMobile,
  deleteTask,
}) => {
  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const newTasks = Array.from(tasks);
    const [movedTask] = newTasks.splice(result.source.index, 1);
    newTasks.splice(result.destination.index, 0, movedTask);
    setTasks(newTasks);

    const destIndex = result.destination.index;
    let newOrder;
    if (destIndex === 0) {
      const first = newTasks[1];
      newOrder = first && first.order ? first.order / 2 : 100;
    } else if (destIndex === newTasks.length - 1) {
      const last = newTasks[newTasks.length - 2];
      newOrder = last && last.order ? last.order + 100 : 100;
    } else {
      const prev = newTasks[destIndex - 1];
      const next = newTasks[destIndex + 1];
      const prevOrder = prev ? prev.order || 0 : 0;
      const nextOrder = next ? next.order || 0 : 0;
      newOrder = (prevOrder + nextOrder) / 2;
    }
    editTask(movedTask.id, { order: newOrder });
  };

  // 右スワイプ（左側から出現）: 完了
  const leadingActions = (task) => (
    <LeadingActions>
      <SwipeAction
        onClick={() => editTask(task.id, { status: "完了" })}
        destructive={false} // ボタンタップ必須
      >
        <Box
          sx={{
            bgcolor: "#4caf50",
            color: "white",
            display: "grid",
            placeContent: "center",
            width: "60px",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <CheckIcon />
            <span style={{ fontSize: "0.7rem", fontWeight: "bold" }}>完了</span>
          </Box>
        </Box>
      </SwipeAction>
    </LeadingActions>
  );

  // 左スワイプ（右側から出現）: 編集・削除
  const trailingActions = (task) => (
    <TrailingActions>
      <SwipeAction
        onClick={() => {
          setEditingTask(task);
          setIsModalOpen(true);
        }}
      >
        <Box
          sx={{
            bgcolor: "#2196f3",
            color: "white",
            display: "grid",
            placeContent: "center",
            width: "60px",
            height: "100%",
          }}
        >
          <EditIcon />
        </Box>
      </SwipeAction>
      <SwipeAction destructive={true} onClick={() => deleteTask(task.id)}>
        <Box
          sx={{
            bgcolor: "#f44336",
            color: "white",
            display: "grid",
            placeContent: "center",
            width: "60px",
            height: "100%",
          }}
        >
          <DeleteIcon />
        </Box>
      </SwipeAction>
    </TrailingActions>
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="taskList">
        {(provided) => (
          <ul
            className="task-list"
            {...provided.droppableProps}
            ref={(el) => {
              provided.innerRef(el);
              if (draggableElRef) draggableElRef.current = el;
            }}
          >
            {tasks.map((task, index) => (
              <Draggable key={task.id} draggableId={task.id} index={index}>
                {(provided, snapshot) => {
                  const content = (
                    <div
                      {...provided.dragHandleProps}
                      className={`task-item status-${statusToSlug(
                        task.status,
                      )} status-${task.status} ${task.start ? "has-time" : ""}`}
                    >
                      <div className="inner">
                        <div
                          onClick={() => {
                            setEditingTask(task);
                            setIsModalOpen(true);
                          }}
                        >
                          <p className="task-title">{task.title}</p>
                          <p className="task-discription">
                            {task.status} / {task.estimatedHours}h
                          </p>
                        </div>
                      </div>
                    </div>
                  );

                  return (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        zIndex: snapshot.isDragging ? 9999 : "auto",
                        marginBottom: isMobile ? 0 : 5,
                      }}
                      className="fc-draggable-event"
                      data-id={task.id}
                      data-title={task.title}
                      data-hours={task.estimatedHours}
                    >
                      {isMobile ? (
                        <SwipeableList
                          fullSwipe={false}
                          type={ListType.IOS}
                          threshold={0.01}
                        >
                          <SwipeableListItem
                            leadingActions={leadingActions(task)}
                            trailingActions={trailingActions(task)}
                          >
                            <div style={{ width: "100%" }}>{content}</div>
                          </SwipeableListItem>
                        </SwipeableList>
                      ) : (
                        content
                      )}
                    </li>
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
};

// --- Page 1: ToDaaay ---
const ToDaaayPage = ({
  tasks,
  holidays,
  addTask,
  editTask,
  deleteTask,
  setEditingTask,
  setIsModalOpen,
}) => {
  const [taskWidth, setTaskWidth] = useState(() => {
    const saved = localStorage.getItem("taskWidth");
    return saved ? Number(saved) : 320;
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState("calendar");
  const [isComposing, setIsComposing] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState("");

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState({
    未対応: true,
    対応中: true,
    一旦: true,
    完了: true,
    保留: true,
    連絡待ち: true,
    対応不要: true,
  });

  const [filterTitle, setFilterTitle] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const isComposingRef = useRef(false);

  const [currentViewRange, setCurrentViewRange] = useState({
    start: new Date(),
    end: new Date(),
  });

  const calendarRef = useRef(null);
  const draggableElRef = useRef(null);
  const statusOptions = Object.keys(filterStatus);

  useEffect(() => {
    localStorage.setItem("taskWidth", taskWidth);
  }, [taskWidth]);

  useEffect(() => {
    const updateViewport = () => {
      const meta = document.querySelector("meta[name='viewport']");
      if (!meta) return;
      const minWidth = 550;
      if (window.outerWidth < minWidth) {
        meta.setAttribute("content", `width=${minWidth}, user-scalable=no`);
      } else {
        meta.setAttribute("content", "width=device-width, initial-scale=1");
      }
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let draggable = null;
    if (draggableElRef.current) {
      draggable = new FcDraggable(draggableElRef.current, {
        itemSelector: ".fc-draggable-event",
        eventData: function (eventEl) {
          return {
            id: eventEl.getAttribute("data-id"),
            title: eventEl.getAttribute("data-title"),
            duration: { hours: eventEl.getAttribute("data-hours") || 1 },
            create: false,
          };
        },
      });
    }
    return () => {
      if (draggable) draggable.destroy();
    };
  }, [tasks, isMobile, activeTab, filterStatus, searchQuery]);

  const startResizing = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = taskWidth;
    const onMouseMove = (e) => {
      const newWidth = startWidth - (e.clientX - startX);
      if (newWidth > 100 && newWidth < 800) setTaskWidth(newWidth);
    };
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const handleEventReceive = (info) => {
    const { id } = info.event;
    const isAllDay = info.event.allDay;
    const start = toDateTimeStr(info.event.start, isAllDay);
    let end = info.event.end ? toDateTimeStr(info.event.end, isAllDay) : null;
    if (!isAllDay && !end && info.event.start) {
      const endDate = new Date(info.event.start.getTime() + 60 * 60 * 1000);
      end = toDateTimeStr(endDate, false);
    }
    info.event.remove();
    editTask(id, { start, end, isAllDay });
  };

  const handleEventChange = (info) => {
    const isAllDay = info.event.allDay;
    const start = toDateTimeStr(info.event.start, isAllDay);
    const end = info.event.end ? toDateTimeStr(info.event.end, isAllDay) : null;
    editTask(info.event.id, { start, end, isAllDay });
  };

  const { filteredTasks } = useMemo(() => {
    const result = tasks.filter(
      (t) =>
        filterStatus[t.status] &&
        t.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    const inbox = [];
    const inView = [];
    result.forEach((t) => {
      if (!t.start) {
        inbox.push(t);
      } else {
        const d = new Date(t.start);
        if (
          (d >= currentViewRange.start && d < currentViewRange.end) ||
          (d < currentViewRange.start && t.status !== "完了")
        ) {
          inView.push(t);
        }
      }
    });
    return { filteredTasks: [...inbox, ...inView] };
  }, [tasks, filterStatus, searchQuery, currentViewRange]);

  const TabButton = ({ isActive, onClick, label }) => (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "10px",
        border: "none",
        cursor: "pointer",
        backgroundColor: isActive ? "#333" : "#f0f0f0",
        color: isActive ? "#fff" : "#333",
        fontWeight: "bold",
      }}
    >
      {label}
    </button>
  );

  const handleQuickAdd = () => {
    const title = quickAddTitle.trim();
    if (!title) return;
    addTask({
      title,
      status: "未対応",
      estimatedHours: 0,
      isAllDay: true,
      start: getTodayDate(),
    });
    setQuickAddTitle("");
  };

  const handleQuickAddKeyDown = (e) => {
    if (e.key === "Enter" && !isComposing) {
      handleQuickAdd();
    }
  };

  const openNewTaskModal = () => {
    setEditingTask({});
    setIsModalOpen(true);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: isMobile ? "0" : "10px",
        overflow: "hidden",
      }}
    >
      {isMobile && (
        <div style={{ display: "flex", borderBottom: "1px solid #ddd" }}>
          <TabButton
            isActive={activeTab === "calendar"}
            onClick={() => setActiveTab("calendar")}
            label="カレンダー"
          />
          <TabButton
            isActive={activeTab === "tasks"}
            onClick={() => setActiveTab("tasks")}
            label="タスクリスト"
          />
        </div>
      )}

      <div
        style={{
          display: isMobile ? "block" : "flex",
          flex: 1,
          gap: "10px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {(!isMobile || activeTab === "calendar") && (
          <div
            style={{
              flex: 1,
              height: "100%",
              padding: isMobile ? "10px" : "0",
            }}
          >
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                interactionPlugin,
                listPlugin,
              ]}
              initialView="timeGridDay"
              locale={jaLocale}
              firstDay={6}
              timeZone="local"
              scrollTime={new Date().toTimeString().slice(0, 8)}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "listDay,dayGridMonth,timeGridWeek,timeGridDay",
              }}
              // buttonText={{
              //   today: "今日",
              //   month: "Block",
              //   week: "Week",
              //   day: "Today",
              //   listMonth: "List",
              // }}
              nowIndicator={true}
              allDaySlot={true}
              slotLabelFormat={{
                hour: "numeric",
                minute: "2-digit",
                hour12: false,
              }}
              height="100%"
              views={{
                dayGridYear: {
                  dayMaxEvents: 1,
                },
                dayGridMonth: {
                  dayMaxEvents: 1,
                },
                timeGridWeek: {
                  dayMaxEvents: 1,
                },
                timeGridDay: {
                  dayMaxEvents: 1,
                },
              }}
              droppable={true}
              eventReceive={handleEventReceive}
              selectable={true}
              selectMirror={true}
              select={(selectInfo) => {
                const startStr = toDateTimeStr(
                  selectInfo.start,
                  selectInfo.allDay,
                );
                const endStr = selectInfo.allDay
                  ? null
                  : toDateTimeStr(selectInfo.end, selectInfo.allDay);
                setEditingTask({
                  title: "",
                  status: "未対応",
                  start: startStr,
                  end: endStr,
                  isAllDay: selectInfo.allDay,
                  estimatedHours: 0,
                  memo: "",
                });
                setIsModalOpen(true);
                selectInfo.view.calendar.unselect();
              }}
              eventClick={(clickInfo) => {
                if (clickInfo.event.id.startsWith("holiday-")) return;
                const t = tasks.find((task) => task.id === clickInfo.event.id);
                if (t) {
                  setEditingTask(t);
                  setIsModalOpen(true);
                }
              }}
              events={[
                ...tasks
                  .filter((t) => t.start)
                  .map((t) => ({
                    id: t.id,
                    title: t.title,
                    start: t.start,
                    end: t.end,
                    allDay: t.isAllDay,
                    extendedProps: {
                      status: t.status,
                      estimatedHours: t.estimatedHours,
                    },
                  })),
                ...holidays,
              ]}
              editable={true}
              eventResizableFromStart={true}
              eventDrop={handleEventChange}
              eventResize={handleEventChange}
              datesSet={(dateInfo) =>
                setCurrentViewRange({
                  start: dateInfo.start,
                  end: dateInfo.end,
                })
              }
            />
          </div>
        )}

        {!isMobile && (
          <div
            className="separator"
            onMouseDown={startResizing}
            style={{ width: "5px", cursor: "col-resize", background: "#ddd" }}
          ></div>
        )}

        {(!isMobile || activeTab === "tasks") && (
          <div
            style={{
              width: isMobile ? "100%" : taskWidth,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              padding: isMobile ? "10px" : "0",
            }}
          >
            {/* --- サイドバーヘッダー --- */}
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                p: 1,
                alignItems: "center",
              }}
            >
              <TextField
                placeholder="クイック追加"
                size="small"
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
                onCompositionStart={() => setIsComposing(true)}
                onCompositionEnd={() => setIsComposing(false)}
                onKeyDown={handleQuickAddKeyDown}
                sx={{ flex: 1 }}
                InputProps={{
                  sx: { paddingRight: 0 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        variant="contained"
                        onClick={handleQuickAdd}
                        disableElevation
                        size="small"
                        sx={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          minWidth: "auto",
                          fontWeight: "bold",
                          boxShadow: "none",
                          height: "40px",
                        }}
                      >
                        追加
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              <Tooltip title="詳細追加">
                <IconButton
                  onClick={openNewTaskModal}
                  color="primary"
                  sx={{ ml: 0.5 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="絞り込み">
                <IconButton
                  onClick={() => setIsFilterOpen(true)}
                  color={
                    Object.values(filterStatus).some((v) => !v) || searchQuery
                      ? "secondary"
                      : "default"
                  }
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* --- 絞り込みモーダル (Dialog) --- */}
            <Dialog
              open={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>タスクの絞り込み</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Box>
                    <Box
                      sx={{
                        mb: 1,
                        fontSize: "0.875rem",
                        color: "text.secondary",
                      }}
                    >
                      ステータス
                    </Box>
                    <FormGroup row>
                      {statusOptions.map((status) => (
                        <FormControlLabel
                          key={status}
                          control={
                            <Checkbox
                              checked={filterStatus[status]}
                              onChange={(e) =>
                                setFilterStatus((prev) => ({
                                  ...prev,
                                  [status]: e.target.checked,
                                }))
                              }
                              size="small"
                            />
                          }
                          label={status}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                  <TextField
                    placeholder="タイトル検索"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={filterTitle}
                    onCompositionStart={() => {
                      isComposingRef.current = true;
                    }}
                    onCompositionEnd={(e) => {
                      isComposingRef.current = false;
                      setSearchQuery(e.target.value);
                    }}
                    onChange={(e) => {
                      setFilterTitle(e.target.value);
                      if (!isComposingRef.current) {
                        setSearchQuery(e.target.value);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setFilterTitle("");
                    setSearchQuery("");
                    setFilterStatus((prev) => {
                      const next = { ...prev };
                      Object.keys(next).forEach((k) => (next[k] = true));
                      return next;
                    });
                  }}
                >
                  リセット
                </Button>
                <Button
                  onClick={() => setIsFilterOpen(false)}
                  variant="contained"
                >
                  完了
                </Button>
              </DialogActions>
            </Dialog>

            {/* --- タスクリスト (DnD + Swipe) --- */}
            <div style={{ flex: 1, overflow: "hidden" }}>
              <TaskList
                tasks={filteredTasks}
                setTasks={(newTasks) => {}}
                setEditingTask={setEditingTask}
                setIsModalOpen={setIsModalOpen}
                editTask={editTask}
                draggableElRef={draggableElRef}
                isMobile={isMobile}
                deleteTask={deleteTask}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Page 2: All Tasks (Backlog) ---
const AllTasksPage = ({ tasks, setEditingTask, setIsModalOpen }) => {
  const [sortField, setSortField] = useState("created");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [dateFilterMode, setDateFilterMode] = useState("all");
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getDateRange = () => {
    if (dateFilterMode === "all") return null;
    if (dateFilterMode === "custom") {
      if (!customStartDate && !customEndDate) return null;
      return { start: customStartDate, end: customEndDate };
    }

    const date = new Date(selectedDate);
    let start, end;

    if (dateFilterMode === "day") {
      start = selectedDate;
      end = selectedDate;
    } else if (dateFilterMode === "week") {
      const dayOfWeek = date.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(date);
      monday.setDate(date.getDate() + diff);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      start = toDateTimeStr(monday, true);
      end = toDateTimeStr(sunday, true);
    } else if (dateFilterMode === "month") {
      const year = date.getFullYear();
      const month = date.getMonth();
      start = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      end = `${year}-${String(month + 1).padStart(2, "0")}-${lastDay}`;
    }

    return { start, end };
  };

  const filteredAndSortedTasks = useMemo(() => {
    let filtered = tasks.filter((task) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        task.title.toLowerCase().includes(searchLower) ||
        (task.memo && task.memo.toLowerCase().includes(searchLower)) ||
        task.status.toLowerCase().includes(searchLower) ||
        (task.start && task.start.includes(searchQuery));

      if (!matchesSearch) return false;

      const dateRange = getDateRange();
      if (!dateRange) return true;

      if (!task.start) return false;

      const taskDate = task.start.slice(0, 10);
      const { start, end } = dateRange;

      if (start && taskDate < start) return false;
      if (end && taskDate > end) return false;

      return true;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case "title":
          aVal = a.title || "";
          bVal = b.title || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "start":
          aVal = a.start || "";
          bVal = b.start || "";
          break;
        case "estimatedHours":
          aVal = a.estimatedHours || 0;
          bVal = b.estimatedHours || 0;
          break;
        case "created":
          aVal = a.created?.seconds || 0;
          bVal = b.created?.seconds || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [
    tasks,
    sortField,
    sortDirection,
    searchQuery,
    dateFilterMode,
    selectedDate,
    customStartDate,
    customEndDate,
  ]);

  const SortableHeader = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        padding: "12px 8px",
        textAlign: "left",
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
      }}
    >
      {children}
      {sortField === field && (
        <span style={{ marginLeft: "4px" }}>
          {sortDirection === "asc" ? "↑" : "↓"}
        </span>
      )}
    </th>
  );

  const getDateFilterLabel = () => {
    switch (dateFilterMode) {
      case "all":
        return "全期間";
      case "day":
        return `${selectedDate}`;
      case "week":
        const date = new Date(selectedDate);
        const dayOfWeek = date.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(date);
        monday.setDate(date.getDate() + diff);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return `${toDateTimeStr(monday, true)} 〜 ${toDateTimeStr(
          sunday,
          true,
        )}`;
      case "month":
        return selectedDate.slice(0, 7);
      case "custom":
        if (!customStartDate && !customEndDate) return "期間指定";
        return `${customStartDate || "..."} 〜 ${customEndDate || "..."}`;
      default:
        return "";
    }
  };

  return (
    <Box
      sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Typography variant="h5" fontWeight="bold" mb={2}>
        All Tasks
      </Typography>

      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          placeholder="タスクを検索（タイトル、メモ、ステータス、日付）"
          size="small"
          fullWidth
          value={searchText}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={(e) => {
            setIsComposing(false);
            setSearchQuery(e.target.value);
          }}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!isComposing) {
              setSearchQuery(e.target.value);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          onClick={() => setIsDateFilterOpen(true)}
          sx={{ minWidth: 200, whiteSpace: "nowrap" }}
        >
          📅 {getDateFilterLabel()}
        </Button>
      </Stack>

      <Dialog
        open={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>日付で絞り込み</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              select
              label="絞り込みモード"
              value={dateFilterMode}
              onChange={(e) => setDateFilterMode(e.target.value)}
              fullWidth
            >
              <MenuItem value="all">全期間</MenuItem>
              <MenuItem value="day">1日</MenuItem>
              <MenuItem value="week">週</MenuItem>
              <MenuItem value="month">月</MenuItem>
              <MenuItem value="custom">カスタム範囲</MenuItem>
            </TextField>

            {(dateFilterMode === "day" ||
              dateFilterMode === "week" ||
              dateFilterMode === "month") && (
              <TextField
                type="date"
                label="基準日"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}

            {dateFilterMode === "custom" && (
              <>
                <TextField
                  type="date"
                  label="開始日"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  label="終了日"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDateFilterMode("all");
              setCustomStartDate("");
              setCustomEndDate("");
            }}
          >
            リセット
          </Button>
          <Button
            onClick={() => setIsDateFilterOpen(false)}
            variant="contained"
          >
            適用
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "#fff",
            }}
          >
            <tr>
              <SortableHeader field="status">ステータス</SortableHeader>
              <SortableHeader field="title">タイトル</SortableHeader>
              <SortableHeader field="start">日付</SortableHeader>
              <SortableHeader field="estimatedHours">工数</SortableHeader>
              <SortableHeader field="created">作成日</SortableHeader>
              <th
                style={{
                  padding: "12px 8px",
                  backgroundColor: "#f5f5f5",
                  fontWeight: "bold",
                }}
              >
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedTasks.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#999",
                  }}
                >
                  タスクが見つかりません
                </td>
              </tr>
            ) : (
              filteredAndSortedTasks.map((t) => (
                <tr
                  key={t.id}
                  style={{
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                  }}
                  onDoubleClick={() => {
                    setEditingTask(t);
                    setIsModalOpen(true);
                  }}
                >
                  <td style={{ padding: "8px" }}>
                    <Box
                      component="span"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        backgroundColor:
                          t.status === "完了" ? "#e8f5e9" : "#fff3e0",
                        color: t.status === "完了" ? "#2e7d32" : "#e65100",
                      }}
                    >
                      {t.status}
                    </Box>
                  </td>
                  <td style={{ padding: "8px" }}>
                    {t.title}
                    {t.memo && (
                      <Box
                        component="div"
                        sx={{ fontSize: "0.75rem", color: "#666", mt: 0.5 }}
                      >
                        {t.memo.slice(0, 50)}
                        {t.memo.length > 50 ? "..." : ""}
                      </Box>
                    )}
                  </td>
                  <td style={{ padding: "8px" }}>
                    {t.start ? (
                      <>
                        {t.start.slice(0, 10)}
                        {!t.isAllDay && t.start.includes("T") && (
                          <Box
                            component="span"
                            sx={{ fontSize: "0.75rem", color: "#666", ml: 0.5 }}
                          >
                            {parseDateTime(t.start).time}
                          </Box>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ padding: "8px" }}>{t.estimatedHours}h</td>
                  <td
                    style={{
                      padding: "8px",
                      fontSize: "0.875rem",
                      color: "#666",
                    }}
                  >
                    {t.created?.seconds
                      ? new Date(t.created.seconds * 1000).toLocaleDateString(
                          "ja-JP",
                        )
                      : "-"}
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTask(t);
                        setIsModalOpen(true);
                      }}
                    >
                      編集
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {filteredAndSortedTasks.length}件のタスクを表示中
      </Typography>
    </Box>
  );
};

// --- Login Screen ---
const LoginScreen = ({ onLogin }) => {
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        bgcolor: "#f5f5f5",
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <DashboardIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
        <Typography variant="h4" fontWeight="bold">
          ToDaay
        </Typography>
        <Typography variant="body1" color="text.secondary">
          今この瞬間に集中する
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="large"
        startIcon={<LoginIcon />}
        onClick={onLogin}
        sx={{
          py: 1.5,
          px: 4,
          fontWeight: "bold",
          fontSize: "1.1rem",
        }}
      >
        Googleでログイン
      </Button>
    </Box>
  );
};

// --- Main App ---
const NAVIGATION = [
  { kind: "header", title: "Main" },
  { segment: "dashboard", title: "ToDaaay", icon: <DashboardIcon /> },
  { segment: "all-tasks", title: "All Tasks", icon: <ListAltIcon /> },
];

export default function DashboardApp() {
  const router = useDemoRouter("/dashboard");
  const [tasks, setTasks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // ★テーマ設定
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode: "light",
      },
    });
  }, []);

  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "tasks"),
      where("uid", "==", user.uid),
      orderBy("order", "asc"),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setTasks(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    fetch("https://holidays-jp.github.io/api/v1/date.json")
      .then((res) => res.json())
      .then((data) => {
        setHolidays(
          Object.keys(data).map((date) => ({
            id: `holiday-${date}`,
            title: data[date],
            start: date,
            allDay: true,
            editable: false,
            color: "#ffebee",
            textColor: "#c62828",
            borderColor: "transparent",
          })),
        );
      });
  }, []);

  const addTask = async (data) => {
    if (!user) return;
    const maxOrder = tasks.length > 0 ? tasks[tasks.length - 1].order || 0 : 0;
    await addDoc(collection(db, "tasks"), {
      ...data,
      order: maxOrder + 100,
      created: serverTimestamp(),
      uid: user.uid,
    });
  };
  const editTask = async (id, data) => updateDoc(doc(db, "tasks", id), data);
  const deleteTask = async (id) => deleteDoc(doc(db, "tasks", id));

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (loadingAuth) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        Loading...
      </Box>
    );
  }

  if (!user) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <AppProvider
      navigation={NAVIGATION}
      router={router}
      theme={theme} // ★テーマ適用
      branding={{ logo: "", title: "ToDaay" }}
    >
      <DashboardLayout
        defaultSidebarCollapsed={true}
        slots={{
          toolbarActions: () => (
            <Stack direction="row" alignItems="center" spacing={1} mr={1}>
              <Avatar
                src={user.photoURL}
                alt={user.displayName}
                sx={{ width: 32, height: 32 }}
              />
              <Tooltip title="ログアウト">
                <IconButton onClick={handleLogout} color="inherit">
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
        }}
      >
        <Box
          sx={{
            height: "calc(100svh - 64px)",
            overflowY: "auto",
            "@media (max-width: 600px)": {
              height: "calc(100svh - 56px)",
            },
          }}
        >
          {(router.pathname === "/dashboard" || router.pathname === "/") && (
            <ToDaaayPage
              tasks={tasks}
              holidays={holidays}
              addTask={addTask}
              editTask={editTask}
              deleteTask={deleteTask}
              setEditingTask={setEditingTask}
              setIsModalOpen={setIsModalOpen}
            />
          )}
          {router.pathname === "/all-tasks" && (
            <AllTasksPage
              tasks={tasks}
              setEditingTask={setEditingTask}
              setIsModalOpen={setIsModalOpen}
            />
          )}
        </Box>
        <TaskModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTask(null);
          }}
          defaultValues={editingTask || {}}
          onSubmit={(data) => {
            if (editingTask && editingTask.id) editTask(editingTask.id, data);
            else addTask(data);
          }}
          onDelete={deleteTask}
        />
      </DashboardLayout>
    </AppProvider>
  );
}
